/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { CloseOutlined, ExclamationCircleFilled, LoadingOutlined, WarningFilled } from '@ant-design/icons';
import { useState } from 'react';

import { JobNotificationType } from '@/client';
import { GuardedComponent } from '@/components/GuardedComponent';
import { useMessage } from '@/hooks/message';
import { useDismissJobNotificationsMutation } from '@/hooks/mutation/useDismissJobNotificationsMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Link } from '@/primitives/Link';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import './JobBanner.css';

export type JobBannerProps = {
  notificationType: JobNotificationType;
  notificationCount: number;
  handleModalOpen: () => void;
};

const statusIcon = {
  running: <LoadingOutlined data-testid="job-running-icon" />,
  warning: <WarningFilled data-testid="job-warning-icon" />,
  error: <ExclamationCircleFilled data-testid="job-error-icon" />,
  timeout: <ExclamationCircleFilled data-testid="job-timeout-icon" />,
};

export function JobBanner({ notificationType, notificationCount, handleModalOpen }: JobBannerProps) {
  const { t } = useMimicTranslation('jobs');
  const [message] = useMessage();
  const [showJobBanner, setShowJobBanner] = useState(true);
  const canDismissJobs = useAuthorization('admin');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate: dismissJobNotifications } = useDismissJobNotificationsMutation({
    onSuccess: () => {
      setShowJobBanner(false);
    },
    onError: () => {
      message.error(t('banner.dismissJobBannerError'));
    },
  });

  const showDismissButton = notificationType !== 'running' && canDismissJobs;
  const jobBannerClasses = ['job-banner', `job-${notificationType}`].join(' ');

  const handleDismiss = () => {
    if (!showDismissButton) return;

    dismissJobNotifications({ status: notificationType as JobNotificationType });
  };

  const handlePauseBannerAlerts = () => {
    handleModalOpen();
  };

  if (!showJobBanner) return null;

  const getNotificationLabel = () => {
    if (notificationType === 'running') return t('banner.runningJob');
    if (notificationType === 'timeout') return t('banner.timedOutJob');
    return t(`banner.${notificationType}`);
  };

  const notification = getNotificationLabel();
  const upperCaseNotificationType = notification.charAt(0).toUpperCase() + notification.slice(1);

  const notificationCountText = () => {
    if (notificationCount === 1) return `${notificationCount} ${t(`banner.${notificationType}`)}`;
    return `${notificationCount} ${t(`banner.${notificationType}`)}s`;
  };

  return (
    <div data-testid={`job-banner-${notificationType}`} className={jobBannerClasses}>
      <div className="job-banner-description">
        {statusIcon[notificationType]}
        <Text size="md" strong>
          {notificationCountText()}
        </Text>
        <Text
          size="md"
          className="job-banner-aggregated-text"
        >{`${t('banner.aggregated', { notificationType: upperCaseNotificationType })}`}</Text>
      </div>
      <div className="job-banner-status">
        <Link to={`/tenants/${tenantId}/jobs`} style={{ padding: '0 8px' }} dataTestId="view-jobs">
          {t('banner.viewJobs')}
        </Link>
        <GuardedComponent requiredRole="admin">
          <Text
            type="link"
            onClick={handlePauseBannerAlerts}
            size="md"
            className="job-banner-pause-text"
            style={{ cursor: 'pointer', padding: '0 8px' }}
            data-testid="pause-all-banner-alerts"
          >
            {t('banner.pauseBannerAlerts')}
          </Text>
        </GuardedComponent>
        <CloseOutlined
          data-testid={`dismiss-${notificationType}`}
          style={{ visibility: showDismissButton ? 'visible' : 'hidden' }}
          onClick={handleDismiss}
        />
      </div>
    </div>
  );
}
